import { DOCUMENT, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { ImageSizes, LinkComponent, SvgComponent } from '@app/base-components';
import { ButtonQaId, HabaQaId } from '@app/helpers';
import { HeaderHeightService, SearchStateService } from '@app/services';
import { Entry } from 'contentful';
import {
  NavigationNode,
  NavigationQuickCall,
} from 'haba-contentful-components/lib/interfaces/navigation';
import { AuthService, I18nModule, WindowRef } from 'haba-core';
import { distinctUntilChanged, filter, startWith, Subject, take, takeUntil } from 'rxjs';
import { NavigationComponent } from 'src/app/header/base/navigation.component';
import { filterShopCategories, isShopNavigationNode } from 'src/app/header/base/helper';
import { RemoveDollarSignPipe } from './removeDollarSign.pipe';
import {
  Category,
  FilterCategory,
  PrefixboxFilterService,
  PrefixboxService,
  SearchResults,
} from 'haba-emporix';
import { ImageComponent } from 'src/app/product/product-card-base/image/image.component';
import { ProductDetailsService } from 'src/app/services/product-details/product-details.service';

@Component({
  selector: 'app-horizontal-menu',
  templateUrl: './horizontal-menu.component.html',
  styleUrls: ['./horizontal-menu.component.scss'],
  imports: [
    NgFor,
    NgIf,
    LinkComponent,
    NgClass,
    I18nModule,
    RemoveDollarSignPipe,
    RouterLink,
    SvgComponent,
    ImageComponent,
    NgTemplateOutlet,
  ],
})
export class HorizontalMenuComponent
  extends NavigationComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  baseId: HabaQaId;
  headerNavigationLink: ButtonQaId;
  @Input() navigationNodes: Entry<NavigationNode>[];
  @Input() quickLinks: Entry<NavigationQuickCall>[];
  @Input() scrolledOnShopRelatedPage: boolean;

  @ViewChildren('flyoutMenu') flyoutMenus: QueryList<ElementRef>;
  @ViewChildren('navigationItem') navigationItems: QueryList<ElementRef>;
  @ViewChildren('shopNavItem') shopNavItems: QueryList<ElementRef>;

  public imageSizes = ImageSizes;

  hoveredItem = null;
  activeNavIndex: number = -1;
  timeOutRef: NodeJS.Timeout;
  private activeTimeoutRef: NodeJS.Timeout;
  private shopNavTimeoutRef: NodeJS.Timeout;
  private closeTimeoutRef: NodeJS.Timeout;
  private readonly HOVER_DELAY_MS = 150;
  private readonly CLOSE_DELAY_MS = 300;
  private readonly DESKTOP_MIN_WIDTH = 992;
  private backgroundScrollLocked = false;
  private backgroundScrollPosition = 0;
  availableFlyoutHeight: number;
  // set number for shop active node, different then any other
  shopNodeIndex = -2;
  shopNavActiveIndex: number = -1;
  mainCategoryActiveIndex: number = -1;
  activePath: number[] = [];
  defaultActiveIndex: number;
  defaultActivePath: number[] = [];
  categoriesActivePath: number[] = [];
  categoryTree;
  private baseCategoryTree: Category[];
  onCategoryPage: boolean;
  onProductPage: boolean;
  currentLevel = 0;
  activePathByKeyboard: number[] = [];
  hoveredActivePath: number[] = [];
  shopNodeFocusCount = 0;

  private lastFocusedByLevel: Record<number, HTMLElement> = {};

  private onDestroy$ = new Subject();

  private handleDocumentClick = (event: MouseEvent) => {
    if (!this.isBrowser) return;
    const target = event.target as HTMLElement;
    const clickedInsideNav = !!target.closest('.main-navigation');
    const clickedInsideFlyout = !!target.closest('.flyout-portal');
    if (!clickedInsideNav && !clickedInsideFlyout) {
      this.closeNavigation();
    }
  };

  constructor(
    @Inject(DOCUMENT) protected documentRef: Document,
    @Inject(PLATFORM_ID) platformId: string,
    authService: AuthService,
    protected winRef: WindowRef,
    protected router: Router,
    elemRef: ElementRef,
    private headerHeightService: HeaderHeightService,
    private cdr: ChangeDetectorRef,
    private prefixboxService: PrefixboxService,
    private prefixboxFilterService: PrefixboxFilterService,
    private productDetailsService: ProductDetailsService,
    private searchStateService: SearchStateService
  ) {
    super(documentRef, platformId, authService, winRef, elemRef, router);
  }

  ngOnChanges() {
    if (this.navigationNodes) {
      this.prefixboxService
        .getCategoryTree()
        .pipe(take(1))
        .subscribe((categoryTree) => {
          if (!this.shopNode) return;

          const filteredCategoryTree = this.prefixboxService.removeEmptyCategories(
            filterShopCategories(categoryTree, this.shopNode)
          );
          this.baseCategoryTree = filteredCategoryTree;
          this.categoryTree = filteredCategoryTree.map((category: Category) => ({
            ...category,
            categoryNode: this.getCategoryNode(filteredCategoryTree, category),
          }));

          this.presetActivePath();
        });
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this.initIds();

    this.searchStateService.results$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((results) => this.applyFilteredCounts(results));
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) return;
    this.initNavigationEvents();
    this.setFlyoutTabindices();
    this.flyoutMenus.changes.subscribe(() => {
      this.setFlyoutTabindices();
    });
    this.headerHeightService.headerHeight$
      .pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe((height) => {
        const offset = 60;
        this.availableFlyoutHeight =
          this.winRef.nativeWindow.innerWidth > 991
            ? this.winRef.nativeWindow.innerHeight - height - offset
            : null;
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(undefined);
    this.onDestroy$.complete();
    if (this.timeOutRef) clearTimeout(this.timeOutRef);
    this.cancelHoverTimer();
    this.cancelShopNavTimer();
    this.cancelCloseTimer();
    this.unlockBackgroundScroll();
    if (this.isBrowser) {
      this.documentRef.removeEventListener('click', this.handleDocumentClick);
    }
  }

  setActive(level: number, index: number) {
    this.cancelCloseTimer();
    this.cancelHoverTimer();
    this.activeTimeoutRef = setTimeout(() => {
      this.activePath = this.activePath.slice(0, level);
      this.activePath[level] = index;
      this.hoveredActivePath = this.hoveredActivePath.slice(0, level);
      this.hoveredActivePath[level] = index;
    }, this.HOVER_DELAY_MS);
  }

  cancelHoverTimer(): void {
    if (this.activeTimeoutRef) clearTimeout(this.activeTimeoutRef);
  }

  cancelCloseTimer(): void {
    if (this.closeTimeoutRef) clearTimeout(this.closeTimeoutRef);
    this.closeTimeoutRef = undefined;
  }

  private scheduleClose(): void {
    this.cancelCloseTimer();
    this.closeTimeoutRef = setTimeout(() => {
      this.closeTimeoutRef = undefined;
      this.closeNavigation();
    }, this.CLOSE_DELAY_MS);
  }

  initNavigationEvents() {
    if (this.isBrowser) {
      this.documentRef.addEventListener('click', this.handleDocumentClick);
    }
    if (this.isBrowser && this.isTouchDevice()) {
      this.closeNavigationOutsideClick();
    }
  }

  clickNavigation(event, item: Entry<NavigationNode>) {
    if (!this.isBrowser) return;
    const target = event.target as HTMLElement;
    const parent: HTMLElement = target.parentNode as HTMLElement;
    target
      .closest('.main-navigation')
      .querySelectorAll('.active')
      .forEach((activeElement) => {
        if (activeElement !== target && !activeElement.contains(target))
          activeElement.classList.remove('active');
      });
    event.preventDefault();
    event.stopPropagation();
    const route = [item.fields?.url];
    this.router.navigate(route, { state: { clearFilters: true } }).then();
    parent.classList.toggle('active');
    this.closeNavigation();
  }

  onMouseEnter(navigationItem: HTMLElement, index: number): void {
    if (!this.isBrowser) return;
    if (this.isTouchDevice()) return;

    if (this.timeOutRef) clearTimeout(this.timeOutRef);
    this.cancelHoverTimer();
    this.cancelCloseTimer();
    this.timeOutRef = setTimeout(() => {
      this.hoveredActivePath = [];
      this.toggleNavigation(navigationItem);
      this.activeNavIndex = index;
      this.shopNavActiveIndex = this.mainCategoryActiveIndex ?? -1;
      this.hoveredItem = navigationItem;
      this.setFlyoutTabindices();
      this.setActivePath();
      this.syncBackgroundScrollLock();
    }, this.HOVER_DELAY_MS);
  }

  onMouseLeave(event?: MouseEvent): void {
    if (!this.isBrowser) return;
    if (this.isTouchDevice()) return;
    if (this.timeOutRef) clearTimeout(this.timeOutRef);
    this.cancelHoverTimer();
    this.cancelShopNavTimer();
    const toElement = (event?.relatedTarget || null) as HTMLElement | null;
    if (toElement && (toElement.closest('.main-navigation') || toElement.closest('.flyout-portal')))
      return;
    this.scheduleClose();
  }

  handleKeyDown(event: KeyboardEvent, index: number): void {
    if (!this.isBrowser) return;

    if (event.key === 'Tab') {
      if (index === this.shopNodeIndex && !event.shiftKey) {
        if (this.timeOutRef) clearTimeout(this.timeOutRef);
        event.preventDefault();
        this.activeNavIndex = index;

        this.shopNavActiveIndex = this.shopNodeFocusCount;
        this.shopNodeFocusCount = ++this.shopNodeFocusCount;

        this.syncBackgroundScrollLock();
        this.cdr.detectChanges();

        const shopItemsCount = this.shopNode.fields.childNavigationNodes.length;
        if (this.shopNavActiveIndex >= shopItemsCount - 1) {
          this.closeNavigation();
          const navItem = this.navigationItems.toArray()[0];
          if (navItem) {
            const link = navItem.nativeElement.querySelector('a, button');
            if (link) {
              link.focus();
            }
          }
          return;
        }

        this.focusShopItem(this.shopNavActiveIndex);

        this.cdr.detectChanges();
        return;
      } else {
        this.shopNodeFocusCount = 0;
      }

      const isLast = index === this.navigationItems.length - 1;
      const isFirst = index === 0;

      if (!event.shiftKey && isLast) {
        this.closeNavigation();
      } else if (event.shiftKey && isFirst) {
        this.closeNavigation();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      if (index === this.shopNodeIndex && !event.shiftKey) {
        event.preventDefault();
        this.activeNavIndex = index;
        this.shopNavActiveIndex = this.shopNavActiveIndex > -1 ? ++this.shopNavActiveIndex - 1 : 0;
        this.syncBackgroundScrollLock();
        this.cdr.detectChanges();
        this.focusFlyoutContent(this.shopNavActiveIndex);
        return;
      }

      event.preventDefault();
      this.activeNavIndex = index;
      this.syncBackgroundScrollLock();
      this.cdr.detectChanges();
      this.focusFlyoutContent(index);
    }
  }

  handleFlyoutKeyDown(event: KeyboardEvent, navIndex: number): void {
    if (!this.isBrowser) return;
    const flyoutMenuArray = this.flyoutMenus.toArray();
    const currentFlyout = flyoutMenuArray.find(
      (menu) => menu.nativeElement.getAttribute('data-nav-index') === navIndex.toString()
    );

    if (!currentFlyout) return;

    const visibleFocusables = Array.from(
      currentFlyout.nativeElement.querySelectorAll('a, button, [role="button"]')
    ) as HTMLElement[];

    const active = this.documentRef.activeElement as HTMLElement;
    const idx = visibleFocusables.findIndex((el) => el === active);

    if (event.key === 'Tab') {
      const focusableElements = Array.from(
        currentFlyout.nativeElement.querySelectorAll(
          'a, button, [role="button"], [tabindex]:not([tabindex="-1"])'
        )
      ) as HTMLElement[];

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const currentElement = this.documentRef.activeElement as HTMLElement;
      const currentIndex = focusableElements.findIndex((el) => el === currentElement);

      if (currentIndex === -1) {
        event.preventDefault();
        focusableElements[0].focus();
        return;
      }

      event.preventDefault();

      const direction = event.shiftKey ? -1 : 1;
      let nextIndex = currentIndex + direction;

      if (nextIndex < 0) nextIndex = focusableElements.length - 1;
      if (nextIndex >= focusableElements.length) nextIndex = 0;

      focusableElements[nextIndex].focus();
      return;
    }

    const focusableElements = Array.from(
      currentFlyout.nativeElement.querySelectorAll('button, a, [role="button"]')
    ) as HTMLElement[];

    if (focusableElements.length === 0) return;

    const currentElement = this.documentRef.activeElement as HTMLElement;
    const currentIndex = focusableElements.findIndex((el) => el === currentElement);

    if (currentIndex === -1) return;

    const isFirstInFlyout = currentIndex === 0;

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      if (isFirstInFlyout) {
        const navigationItemsArray =
          this.shopNavActiveIndex > -1
            ? this.shopNavItems.toArray()
            : this.navigationItems.toArray();
        const targetNavItem = navigationItemsArray[navIndex];

        if (targetNavItem) {
          const nativeLink = targetNavItem.nativeElement.querySelector(
            'app-link a, app-link span[role="button"]'
          ) as HTMLElement;

          if (nativeLink) {
            nativeLink.focus();
          }
        }
      } else {
        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0 && focusableElements[prevIndex]) {
          focusableElements[prevIndex].focus();
        }
      }
      return;
    }

    if (['ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'].includes(event.key)) {
      event.preventDefault();

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();

        if (!visibleFocusables.length) return;

        if (idx === -1) {
          visibleFocusables[0].focus();
          return;
        }

        const dir = event.key === 'ArrowDown' ? 1 : -1;
        let next = idx + dir;

        if (next < 0) next = visibleFocusables.length - 1;
        if (next >= visibleFocusables.length) next = 0;

        visibleFocusables[next].focus();
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();

        const active = this.documentRef.activeElement as HTMLElement;
        if (!active) return;

        const level = this.getCurrentLevel(active);
        const li = active.closest('li');
        if (!li) return;

        this.lastFocusedByLevel[level] = active;

        const siblings = Array.from(li.parentElement?.children || []);
        const rowIndex = siblings.indexOf(li) - 1;

        this.hoveredActivePath[level] = rowIndex;
        this.activePathByKeyboard[level] = rowIndex;

        this.cdr.detectChanges();

        setTimeout(() => {
          this.focusFirstOfLevel(level + 1);
        });

        return;
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();

        const active = this.documentRef.activeElement as HTMLElement;
        if (!active) return;

        const level = this.getCurrentLevel(active);
        if (level === 0) return;

        this.hoveredActivePath.splice(level - 1);
        this.activePathByKeyboard.splice(level - 1);

        this.cdr.detectChanges();

        setTimeout(() => {
          const prev = this.lastFocusedByLevel[level - 1];
          if (prev) {
            prev.focus();
          } else {
            this.focusFirstOfLevel(level - 1);
          }
        });

        return;
      }
    }

    if (event.key === 'Escape') {
      this.closeNavigation();
    }
  }

  onHoverShopNavLink(index: number) {
    this.cancelCloseTimer();
    this.cancelShopNavTimer();
    this.shopNavTimeoutRef = setTimeout(() => {
      this.hoveredActivePath = [];
      this.shopNavActiveIndex = index;
      this.setActivePath();
      this.syncBackgroundScrollLock();
    }, this.HOVER_DELAY_MS);
  }

  cancelShopNavTimer(): void {
    if (this.shopNavTimeoutRef) clearTimeout(this.shopNavTimeoutRef);
  }

  isShopNode(navigationNode: Entry<NavigationNode>): boolean {
    return isShopNavigationNode(navigationNode);
  }

  get shopNode(): Entry<NavigationNode> {
    return this.navigationNodes?.find(isShopNavigationNode);
  }

  get isShopRelatedPage() {
    const urlSegments = this.router.url.split('/');
    return (
      urlSegments[1] === 'shop' ||
      urlSegments[1] === 'c' ||
      urlSegments[1] === 'p' ||
      urlSegments[1] === 'cart'
    );
  }

  get onMobile(): boolean {
    if (!this.isBrowser) return;
    return this.winRef.nativeWindow.innerWidth < 992;
  }

  private getCategoryNode(categoryTree: Category[], category: Category) {
    let result = this.prefixboxService.findByCode(categoryTree, category.code);

    if (!this.shopNode) return;

    const node = result
      ? this.shopNode.fields.childNavigationNodes.find(
          (node) => node.fields.url === result.category.url
        )
      : undefined;

    if (node?.fields?.cloudinaryImage || node?.fields?.cloudinaryImage2) {
      return node;
    }

    return this.shopNode;
  }

  private focusShopItem(itemIndex: number): void {
    const flyoutMenuArray = this.flyoutMenus.toArray();
    const shopFlyout = flyoutMenuArray.find(
      (menu) => menu.nativeElement.getAttribute('data-nav-index') === this.shopNodeIndex.toString()
    );

    if (!shopFlyout) return;

    const focusableItems = Array.from(
      shopFlyout.nativeElement.querySelectorAll('button, a, [role="button"]')
    ) as HTMLElement[];

    if (focusableItems[itemIndex]) {
      focusableItems[itemIndex].focus();
      this.cdr.detectChanges();
    }
  }

  private focusFlyoutContent(index: number): void {
    const flyoutMenuArray = this.flyoutMenus.toArray();

    const targetFlyout = flyoutMenuArray.find(
      (menu) => menu.nativeElement.getAttribute('data-nav-index') === index.toString()
    );

    if (targetFlyout) {
      const firstFocusableElement = targetFlyout.nativeElement.querySelector(
        'button, a, [role="button"]'
      ) as HTMLElement;

      if (firstFocusableElement) {
        this.activePathByKeyboard = [];
        firstFocusableElement.focus();
      }
    }
  }

  private presetActivePath() {
    this.router.events
      .pipe(
        takeUntil(this.onDestroy$),
        filter((event) => event instanceof NavigationEnd),
        startWith(null)
      )
      .subscribe(() => {
        (document.activeElement as HTMLElement)?.blur();
        this.closeNavigation();

        const [, pageType, lvl0, lvl1, lvl2, lvl3] = this.router.url.split('/');
        this.onCategoryPage = pageType === 'c';
        this.onProductPage = pageType === 'p';

        this.mainCategoryActiveIndex = -1;
        this.categoriesActivePath = [];

        this.defaultActiveIndex = null;
        this.defaultActivePath = [];

        if (this.onProductPage) {
          this.productDetailsService.productCategoryPath$
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((productCategoryPath) => {
              const [main, ...path] = this.getProductIndexPath(
                this.categoryTree,
                productCategoryPath
              );
              this.mainCategoryActiveIndex = main;
              this.categoriesActivePath = path;
            });
        } else if (this.onCategoryPage) {
          this.mainCategoryActiveIndex = this.categoryTree.findIndex(
            (category) => lvl0?.length && category.url.includes(lvl0)
          );
          const mainCategory = this.categoryTree[this.mainCategoryActiveIndex];

          if (lvl1) {
            this.categoriesActivePath = [
              ...this.categoriesActivePath,
              mainCategory.subcategories.findIndex((category) =>
                category.url.includes(`/${lvl0}/${lvl1}`)
              ),
            ];
          }

          if (lvl2) {
            this.categoriesActivePath = [
              ...this.categoriesActivePath,
              mainCategory.subcategories[this.categoriesActivePath[0]].subcategories.findIndex(
                (category) => category.url.includes(`/${lvl0}/${lvl1}/${lvl2}`)
              ),
            ];
          }

          if (lvl3) {
            this.categoriesActivePath = [
              ...this.categoriesActivePath,
              mainCategory.subcategories[this.categoriesActivePath[0]].subcategories[
                this.categoriesActivePath[1]
              ].subcategories.findIndex((category) =>
                category.url.includes(`/${lvl0}/${lvl1}/${lvl2}/${lvl3}`)
              ),
            ];
          }
        } else {
          const path = this.router.url?.split('/')?.slice(1)?.join('/');
          const result = this.findIndexPathByUrl(this.navigationNodes, `/${path}`) ?? [];
          // have to remove the activeNavIndex
          this.defaultActiveIndex = result[0];
          this.defaultActivePath = result.slice(1);
        }
      });
  }

  findIndexPathByUrl(nodes: any[], search: string, path: number[] = []): number[] | null {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const url = node?.fields?.url;
      const children = node?.fields?.childNavigationNodes;

      if (url && url === search) {
        return [...path, i];
      }

      if (children?.length) {
        const result = this.findIndexPathByUrl(children, search, [...path, i]);
        if (result) return result;
      }
    }
    return null;
  }

  get currentPath() {
    const [noQueryParamsMainCategory] = this.router.url.split('?');
    const [, pageType, mainCategory] = noQueryParamsMainCategory.split('/');
    if (!mainCategory) return;
    return `/${pageType}/${mainCategory}`;
  }

  private initIds() {
    this.baseId = HabaQaId.forPage(HabaQaId.page.header);
    this.headerNavigationLink = this.baseId.getButtonId(
      HabaQaId.elements.button.headerNavigationLink
    );
  }

  private setFlyoutTabindices(): void {
    if (!this.isBrowser) return;
    if (!this.flyoutMenus) return;
    this.flyoutMenus.forEach((flyout, index) => {
      const isActive = this.activeNavIndex === index;
      const focusableElements = flyout.nativeElement.querySelectorAll('a, button, [role="button"]');
      focusableElements.forEach((el: HTMLElement) => {
        el.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    });
  }

  private getCurrentLevel(el: HTMLElement): number {
    const lvl = el.closest('ul.nav-nodes-container');
    if (!lvl) return 0;

    if (lvl.classList.contains('lvl-0')) return 0;
    if (lvl.classList.contains('lvl-1')) return 1;
    if (lvl.classList.contains('lvl-2')) return 2;

    return 0;
  }

  private focusFirstOfLevel(level: number): void {
    const container = this.documentRef.querySelector(
      `.nav-nodes-container.lvl-${level}`
    ) as HTMLElement;

    if (!container) return;

    const first = container.querySelector('a, button, [role="button"]') as HTMLElement;

    first?.focus();
  }

  private getProductIndexPath(tree: any[], path: string[]): number[] {
    const indexes: number[] = [];
    let currentLevel = tree;

    for (const url of path) {
      const index = currentLevel.findIndex((cat) => cat.url === url);

      if (index === -1) {
        break;
      }

      indexes.push(index);
      currentLevel = currentLevel[index].subcategories || [];
    }

    return indexes;
  }

  private setActivePath() {
    this.activePath =
      this.shopNavActiveIndex === this.mainCategoryActiveIndex &&
      (this.onCategoryPage || this.onProductPage)
        ? this.categoriesActivePath
        : this.activeNavIndex === this.defaultActiveIndex
          ? this.defaultActivePath
          : [];
  }

  private applyFilteredCounts(results: SearchResults | null): void {
    if (!this.baseCategoryTree) return;

    if (!results || !this.hasFilters(results)) {
      this.categoryTree = this.baseCategoryTree.map((category: Category) => ({
        ...category,
        categoryNode: this.getCategoryNode(this.baseCategoryTree, category),
      }));
      this.cdr.detectChanges();
      return;
    }

    const [, , lvl0Raw] = this.router.url.split('/');
    const lvl0 = lvl0Raw?.split('?')[0];
    const filterValues = results.filters?.find((f) => f.name === 'category')?.values || [];
    const filterTree = this.prefixboxFilterService.createCategoryListFilter(filterValues) || [];

    this.categoryTree = this.baseCategoryTree.map((category: Category) => {
      const categoryNode = this.getCategoryNode(this.baseCategoryTree, category);

      if (!lvl0 || !category.url.includes(lvl0)) {
        return { ...category, categoryNode };
      }

      const match = filterTree.find((fc) => fc.name === category.name);
      return {
        ...category,
        count: match ? match.count : 0,
        subcategories: this.filterCategoryTree(
          category.subcategories || [],
          match?.subCategories || []
        ),
        categoryNode,
      };
    });

    this.cdr.detectChanges();
  }

  private hasFilters(results: SearchResults): boolean {
    return (
      (results.searchParameters?.filters?.rangeFilters?.length ?? 0) > 0 ||
      (results.searchParameters?.filters?.termFilters?.length ?? 0) > 0
    );
  }

  private filterCategoryTree(categories: Category[], filterTree: FilterCategory[]): Category[] {
    return categories.map((category: Category) => {
      const match = filterTree.find((fc) => fc.name === category.name);
      const updatedSubcategories = this.filterCategoryTree(
        category.subcategories || [],
        match?.subCategories || []
      );
      return {
        ...category,
        count: match ? match.count : 0,
        subcategories: updatedSubcategories,
      };
    });
  }

  trackByCode(_index: number, item: Category): string {
    return item?.code ?? String(_index);
  }

  private get isOverlayOpen(): boolean {
    return this.activeNavIndex > -1 || this.shopNavActiveIndex > -1;
  }

  private syncBackgroundScrollLock(): void {
    if (this.isOverlayOpen) {
      this.lockBackgroundScroll();
    } else {
      this.unlockBackgroundScroll();
    }
  }

  private lockBackgroundScroll(): void {
    if (!this.isBrowser || this.backgroundScrollLocked) return;

    const win = this.winRef.nativeWindow;
    if (win.innerWidth < this.DESKTOP_MIN_WIDTH) return;

    const body = this.documentRef.body;
    const scrollbarWidth = win.innerWidth - this.documentRef.documentElement.clientWidth;

    this.backgroundScrollPosition = win.scrollY ?? this.documentRef.documentElement.scrollTop;

    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }
    body.style.overflowY = 'hidden';
    this.backgroundScrollLocked = true;
  }

  private unlockBackgroundScroll(): void {
    if (!this.isBrowser || !this.backgroundScrollLocked) return;

    const body = this.documentRef.body;
    body.style.overflowY = '';
    body.style.paddingRight = '';
    this.backgroundScrollLocked = false;
    this.winRef.nativeWindow.scrollTo(0, this.backgroundScrollPosition);
  }

  closeNavigation(): void {
    if (this.timeOutRef) clearTimeout(this.timeOutRef);
    this.cancelHoverTimer();
    this.cancelShopNavTimer();
    this.cancelCloseTimer();
    this.activeNavIndex = -1;
    this.shopNavActiveIndex = -1;
    this.hoveredItem = null;
    this.hoveredActivePath = [];
    this.activePathByKeyboard = [];
    this.setActivePath();
    this.setFlyoutTabindices();
    this.unlockBackgroundScroll();
  }

  goToCategoryPage(event) {
    const target = event.target as HTMLElement;
    const parent: HTMLElement = target.parentNode as HTMLElement;
    target
      .closest('.main-navigation')
      ?.querySelectorAll('.active')
      ?.forEach((activeElement) => {
        if (activeElement !== target && !activeElement.contains(target))
          activeElement.classList.remove('active');
      });
    parent.classList.toggle('active');
    event.preventDefault();
    event.stopPropagation();
    this.closeNavigation();
  }
}
